import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LegacyEventTemplatesManager from './LegacyEventTemplatesManager';
import EventTemplateList from './EventTemplateList';
import EventTemplateEditPanel from './EventTemplateEditPanel';
import { toastError } from '../../../shared/toastHelper';

const shouldDiscardLineItem = (li) => (
  (li.category_id === null || li.category_id === '')
  && (li.description === null || li.description === '')
  && (li.amount === null || li.amount === '' || li.amount === '0.00')
  && (li.estimate === null || li.estimate === '' || li.estimate === '0.00')
  && (li.actual === null || li.actual === '' || li.actual === '0.00')
);

const EventTemplatesManager = (props) => {
  const {
    csrfToken, teamId, team, eventTemplates, eventTemplatesChanged,
    eventTemplateForForm, eventTemplateForFormChanged, noteTemplates, noteTemplatesChanged,
    createEventTemplate, updateEventTemplate, deleteEventTemplate,
  } = props;

  const [offerTemplates, setOfferTemplates] = useState([]);
  const [budgetTemplates, setBudgetTemplates] = useState([]);

  useEffect(() => {
    if (!team.package.offer_v3) return;

    axios.get(`/teams/${teamId}/offer_templates/v3.json`)
      .then((res) => setOfferTemplates((res.data || []).map((t) => ({ value: t.id, label: t.name }))))
      .catch(() => toastError("Couldn't load offer templates."));
    axios.get(`/teams/${teamId}/budget_templates/v3.json`)
      .then((res) => setBudgetTemplates((res.data || []).map((t) => ({ value: t.id, label: t.name }))))
      .catch(() => toastError("Couldn't load budget templates."));
  }, [teamId, team.package.offer_v3]);

  if (!team.package.offer_v3) {
    return <LegacyEventTemplatesManager {...props} />;
  }

  const startNewTemplate = () => {
    const newEventTemplate = {
      id: `new-${eventTemplates.length}`, name: '', description: '', age_restriction: null, notes: [],
      v3_offer_template_id: null, v3_budget_template_id: null,
      income_template: { is_income: true, finance_line_items: [] },
      expense_template: { is_income: false, finance_line_items: [] },
    };
    eventTemplateForFormChanged(newEventTemplate);
    eventTemplatesChanged([...eventTemplates, newEventTemplate]);
  };

  const cancelEdit = () => {
    if (eventTemplateForForm.id && eventTemplateForForm.id.toString().includes('new-')) {
      eventTemplatesChanged(eventTemplates.filter((et) => !et.id.toString().includes('new-')));
    }
    eventTemplateForFormChanged({});
  };

  const save = (form) => {
    const payload = {
      ...form,
      ...(form.income_template ? { income_template: { ...form.income_template, finance_line_items: (form.income_template.finance_line_items || []).filter((li) => !shouldDiscardLineItem(li)) } } : {}),
      ...(form.expense_template ? { expense_template: { ...form.expense_template, finance_line_items: (form.expense_template.finance_line_items || []).filter((li) => !shouldDiscardLineItem(li)) } } : {}),
    };
    if (form.id > 0) {
      updateEventTemplate(csrfToken, teamId, payload, eventTemplates);
    } else {
      createEventTemplate(csrfToken, teamId, payload, eventTemplates);
    }
  };

  return (
    <EventTemplateList
      eventTemplates={eventTemplates}
      editingId={eventTemplateForForm.id}
      onEdit={(et) => eventTemplateForFormChanged(et)}
      onDelete={(et) => deleteEventTemplate(csrfToken, teamId, et, eventTemplates)}
      onNewTemplate={startNewTemplate}
      renderEditPanel={() => (
        <EventTemplateEditPanel
          eventTemplateForForm={eventTemplateForForm}
          eventTemplateForFormChanged={eventTemplateForFormChanged}
          offerTemplates={offerTemplates}
          budgetTemplates={budgetTemplates}
          noteTemplates={noteTemplates}
          noteTemplatesChanged={noteTemplatesChanged}
          teamId={teamId}
          csrfToken={csrfToken}
          onCancel={cancelEdit}
          onSave={save}
        />
      )}
    />
  );
};

EventTemplatesManager.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  team: PropTypes.object.isRequired,
  eventTemplates: PropTypes.array.isRequired,
  eventTemplatesChanged: PropTypes.func.isRequired,
  eventTemplateForForm: PropTypes.object.isRequired,
  eventTemplateForFormChanged: PropTypes.func.isRequired,
  noteTemplates: PropTypes.array.isRequired,
  noteTemplatesChanged: PropTypes.func.isRequired,
  createEventTemplate: PropTypes.func.isRequired,
  updateEventTemplate: PropTypes.func.isRequired,
  deleteEventTemplate: PropTypes.func.isRequired,
};

export default EventTemplatesManager;
